import { nearestSixteenth } from '../../../helpers';

import type { BreakpointPlacementCandidate, CountertopSection } from '../types';
import type { CountertopSeamGroup } from './get-countertop-seam-groups';

export type BuildPlacementLadderParams = {
	section: CountertopSection;
	groups: Array<CountertopSeamGroup>;
	/**
	 * When set, the ladder is restricted to that group's own rungs -- its centre and its two
	 * edges. Used for the granite rule, where the seam must land ON the appliance and a
	 * run-centre or thirds split would not satisfy it.
	 */
	anchorGroupId?: string | null;
};

const snapped = (centers: Array<number>): Array<number> => centers.map((center) => nearestSixteenth(center));

const byDistanceFromMidpoint = (section: CountertopSection) => {
	const midpoint = (section.start + section.end) / 2;
	return (a: CountertopSeamGroup, b: CountertopSeamGroup): number => Math.abs(a.center - midpoint) - Math.abs(b.center - midpoint);
};

const centerRung = (seamGroup: CountertopSeamGroup): BreakpointPlacementCandidate => ({
	step: seamGroup.isGrill ? 'grill-center' : 'cutout-center',
	centers: snapped([seamGroup.center]),
	anchorGroupId: seamGroup.group.id
});

const edgeRungs = (seamGroup: CountertopSeamGroup): Array<BreakpointPlacementCandidate> => {
	const step = seamGroup.isGrill ? 'grill-edge' : 'cutout-edge';
	return [seamGroup.left, seamGroup.right].map((edge) => ({
		step,
		centers: snapped([edge]),
		anchorGroupId: seamGroup.group.id
	} as BreakpointPlacementCandidate));
};

/**
 * The fall-through order the auto-placement engine walks for one section, exactly as specified:
 *
 * 1. the centre of a grill
 * 2. failing a grill, the centre of whatever else cuts into the countertop
 * 3. failing any cutout at all, the centre of the run
 * 4. failing the grill's centre, that grill's edge -- the Align Left / Align Right behaviour
 * 5. failing every cutout, the centre of the panel
 * 6. failing centring, a split into thirds
 *
 * Caller picks the first rung whose result respects the triggering cap; the ladder itself is pure.
 * @param params the section to divide, the cutouts available on it, and an optional pinned anchor
 * @returns candidates in fall-through order
 */
export const buildPlacementLadder = (params: BuildPlacementLadderParams): Array<BreakpointPlacementCandidate> => {
	const { section, groups, anchorGroupId = null } = params;
	const { start, end, length } = section;

	const inside = groups
		.filter((seamGroup) => seamGroup.center > start && seamGroup.center < end)
		.sort(byDistanceFromMidpoint(section));

	if (anchorGroupId) {
		const anchor = inside.find((seamGroup) => seamGroup.group.id === anchorGroupId) ?? null;
		return anchor ? [centerRung(anchor), ...edgeRungs(anchor)] : [];
	}

	const grills = inside.filter((seamGroup) => seamGroup.isGrill);
	const cutouts = inside.filter((seamGroup) => !seamGroup.isGrill);

	const candidates: Array<BreakpointPlacementCandidate> = [
		...grills.map(centerRung),
		...grills.flatMap(edgeRungs),
		...cutouts.map(centerRung),
		...cutouts.flatMap(edgeRungs)
	];

	// step 3 when the countertop is bare, step 5 once every cutout has been tried
	candidates.push(inside.length === 0
		? { step: 'run-center', centers: snapped([(start + end) / 2]), anchorGroupId: null }
		: { step: 'panel-center', centers: snapped([0]), anchorGroupId: null });

	candidates.push({ step: 'thirds', centers: snapped([start + (length / 3), start + ((length * 2) / 3)]), anchorGroupId: null });

	return candidates;
};
